import React from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Badge,
    Button,
    Breadcrumb,
    BreadcrumbItem
} from "reactstrap";
import { useNavigate } from "react-router-dom";

// Import NightMode context if needed
// import { useNightMode } from "../../../contexts/NightModeContext";

// Main Reports page component
const Reports = () => {
    const navigate = useNavigate();
    // const { isNightMode } = useNightMode(); // Uncomment if you want to support night mode

    // Set the page title
    document.title = "Reports List | EventoX";

    // List of reports to display
    const reportsList = [
        {
            title: "Attendee & Door List Report",
            description: "Generate comprehensive attendee lists with contact details, ticket information, seating arrangements, and check-in status. Perfect for event day management and guest verification.",
            iconClass: "bx-group",
            link: "/event/reports/attendee-report",
            color: "primary",
            gradient: "bg-gradient-primary",
            features: ["Contact Details", "Seating Info", "Check-in Status", "Export Options"]
        },
        // {
        //     title: "Event Performance Summary",
        //     description: "Get detailed insights into event performance including ticket sales, capacity utilization, session attendance, and overall event success metrics across all your events.",
        //     iconClass: "bx-bar-chart-alt-2",
        //     link: "/event/reports/event-summery-report",
        //     color: "success",
        //     gradient: "bg-gradient-success",
        //     features: ["Sales Analytics", "Capacity Metrics", "Success Indicators", "Performance Trends"]
        // },
        {
            title: "Revenue Analytics Dashboard",
            description: "Comprehensive revenue analysis showing total earnings, revenue breakdown by event, ticket type performance, and financial trends with detailed profit/loss insights.",
            iconClass: "bx-dollar-circle",
            link: "/event/reports/event-revenue-report",
            color: "warning",
            gradient: "bg-gradient-warning",
            features: ["Revenue Breakdown", "Profit Analysis", "Financial Trends", "Export Reports"]
        },
        {
            title: "Booking Status & Revenue Report",
            description: "Detailed breakdown of ticket sales performance, revenue by ticket categories, booking statistics, and sales trends with exportable data for financial reporting.",
            iconClass: "bx-ticket",
            link: "/event/reports/booking-status-report",
            color: "info",
            gradient: "bg-gradient-info",
            features: ["Sales Performance", "Category Analysis", "Booking Stats", "Data Export"]
        },
       {
            title: "Ticket Sales Report & Insights",
            description: "Detailed reporting tool for analyzing ticket sales by  ticket type, group, date range, venue. Includes exportable reports, real-time metrics , and  advanced  filtering.   ",
            iconClass: "bx-bar-chart-alt",
            link: "/event/reports/ticket-report",
            color: "danger",
            gradient: "bg-gradient-danger",
            features: ["Sales Breakdown","Tickets Sold","Downloadable Reports","Real-time Analytics"]
        }

        // {
        //     title: "Revenue Report by Date Range",
        //     description: "Track revenue performance within specific time periods. Analyze daily, weekly, or monthly revenue trends with detailed breakdowns of Eventox revenue, organizer revenue, and processing fees.",
        //     iconClass: "bx-calendar-check",
        //     link: "/event/reports/revenue-date-report",
        //     color: "danger",
        //     gradient: "bg-gradient-danger",
        //     features: ["Time-based Analysis", "Revenue Trends", "Fee Breakdown", "Periodic Reports"]
        // }
    ];

    // Handle navigation to report pages
    const onNavigateReport = (url: string) => {
        navigate(url);
    };

    return (
        <div className="page-content">
            <Container fluid>
                {/* Breadcrumb Section */}
                <div className="mb-4">
                    {/* <Breadcrumb>
                        <BreadcrumbItem active>Reports & Analytics</BreadcrumbItem>
                        <BreadcrumbItem>Reports Dashboard</BreadcrumbItem>
                    </Breadcrumb> */}

                    {/* Hero Section */}
                    <div className="text-center mb-5">
                        <div className="bg-gradient-primary rounded-3 p-4 mb-4">
                            <h1 className="mb-3 fw-bold">
                                <i className="bx bx-bar-chart-alt-2 me-3"></i>
                                Reports & Analytics Center
                            </h1>
                            <p className="mb-0 fs-5">
                                Comprehensive insights and analytics to drive your event business forward
                            </p>
                        </div>
                    </div>
                </div>

                {/* Reports Grid Section */}
                <Row className="g-2">
                    {reportsList.map((report, key) => (
                        <Col xl={4} lg={3} md={3} sm={"12"} key={key}>
                            <Card
                                className="report-card h-100 shadow-sm hover-lift transition-all"
                                onClick={() => onNavigateReport(report.link)}
                                style={{ cursor: 'pointer' }}
                            >
                                <CardBody className="p-4">
                                    {/* Card Header with Icon and Badge */}
                                    <div className="d-flex align-items-center mb-4">
                                        <div className={`${report.gradient} rounded-circle p-3 me-3`}>
                                            <i className={`bx ${report.iconClass} fs-3`}></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-1 fw-bold">
                                                {report.title}
                                            </h5>
                                            <Badge color={report.color} className="fs-6">
                                                <i className="bx bx-chart me-1"></i>
                                                Analytics
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Card Description */}
                                    <p className="text-muted mb-4 fs-6 lh-base">
                                        {report.description}
                                    </p>

                                    {/* Features List */}
                                    <div className="mb-4">
                                        <h6 className="fw-semibold text-dark mb-3">
                                            <i className="bx bx-check-circle me-2 text-success"></i>
                                            Key Features:
                                        </h6>
                                        <div className="row g-2">
                                            {report.features.map((feature, index) => (
                                                <div className="col-6" key={index}>
                                                    <div className="d-flex align-items-center">
                                                        <i className="bx bx-check text-success me-2"></i>
                                                        <span className="fs-7 text-muted">{feature}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className="mt-auto">
                                        <Button
                                            color={report.color}
                                            className="w-100 btn-lg fw-semibold"
                                            onClick={e => {
                                                e.stopPropagation();
                                                onNavigateReport(report.link);
                                            }}
                                        >
                                            <i className="bx bx-right-arrow-alt me-2"></i>
                                            Access Report
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Quick Stats Section */}
                <Row className="mt-5">
                    <Col lg={12}>
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-4">
                                <h4 className="card-title mb-4">
                                    <i className="bx bx-stats me-2 text-primary"></i>
                                    Quick Overview
                                </h4>
                                <Row className="g-3">
                                    <Col md={3} sm={6}>
                                        <div className="text-center p-3 border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-white shadow">
                                            <i className="bx bx-group fs-2 text-primary mb-2"></i>
                                            <h5 className="mb-1 fw-bold">4</h5>
                                            <p className="text-muted mb-0">Available Reports</p>
                                        </div>
                                    </Col>
                                    <Col md={3} sm={6}>
                                        <div className="text-center p-3 border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-white shadow">
                                            <i className="bx bx-dollar-circle fs-2 text-success mb-2"></i>
                                            <h5 className="mb-1 fw-bold">Revenue</h5>
                                            <p className="text-muted mb-0">Financial Analytics</p>
                                        </div>
                                    </Col>
                                    <Col md={3} sm={6}>
                                        <div className="text-center p-3 border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-white shadow">
                                            <i className="bx bx-ticket fs-2 text-warning mb-2"></i>
                                            <h5 className="mb-1 fw-bold">Sales</h5>
                                            <p className="text-muted mb-0">Ticket Performance</p>
                                        </div>
                                    </Col>
                                    {/* <Col md={3} sm={6}>
                                        <div className="text-center p-3 border rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-white shadow">
                                            <i className="bx bx-search-alt fs-2 text-info mb-2"></i>
                                            <h5 className="mb-1 fw-bold">Search</h5>
                                            <p className="text-muted mb-0">Advanced Analytics</p>
                                        </div>
                                    </Col> */}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Reports;

